import { DateTime } from "luxon";
import { z } from "zod/v4-mini";

import { TelemonitoringTier, telemonitoringTierSchema } from "@/models/CarePlanModel.ts";
import {
  ConditionType,
  conditionTypeSchema,
  MedicationSchema,
  medicationSchemeSchema,
} from "@/models/ConditionModel.ts";
import { FileModel, fileSchema } from "@/models/FileModel.ts";
import { type PumpDeviceBrand, pumpDeviceBrandSchema } from "@/models/MedicalDeviceModel.ts";
import { TeleMonitoringCriteria, telemonitoringCriteriaSchema } from "@/models/TelemonitoringCriteriaModel.ts";
import { SimpleCarePlanCreationInfo } from "@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanCreation.ts";
import {
  termDateInFutureIfGestational,
  termDateIsValidIfGestational,
} from "@/uiKit/organisms/PatientCreationAndRenewal/termDateRules.ts";
import { cz } from "@/utils/zodUtils.ts";

export type NurseInclusionPatientCreationInfo = {
  givenName: string;
  familyName: string;
  email: string;
  phone: string;
  helperPhone?: string;
  birthDate: string;
  diabetesType: ConditionType;
  insulinScheme: MedicationSchema;
  pump?: PumpDeviceBrand;
  homeServiceProviderId?: string;
  inclusionCriteria?: TeleMonitoringCriteria;
  duration?: "1" | "2" | "3";
  description?: string;
  descriptionAttachment?: FileModel;
  treatmentPrescriptionAttachment?: FileModel;
  referralLetterAttachment?: FileModel;

  termDate?: string;
  periodStart?: string;
  inclusionTier?: TelemonitoringTier;
  telemonitoringNurseId?: string;
};

/** Narrower type for the insulinated path where care plan fields are guaranteed present. */
export type NurseInclusionPatientCreationInfoWithCarePlan = NurseInclusionPatientCreationInfo & {
  inclusionCriteria: TeleMonitoringCriteria;
  duration: "1" | "2" | "3";
  periodStart: string;
  inclusionTier: TelemonitoringTier;
};

export function isNurseInclusionWithCarePlan(
  data: NurseInclusionPatientCreationInfo,
): data is NurseInclusionPatientCreationInfoWithCarePlan {
  return !!data.inclusionCriteria && !!data.duration && !!data.periodStart && !!data.inclusionTier;
}

const requiredHSPIfPump = (data: SimpleCarePlanCreationInfo) => {
  if (data.insulinScheme === "insulinPump") {
    return !!data.homeServiceProviderId;
  }
  return true;
};

const requiredTermDateIfGestational = (data: SimpleCarePlanCreationInfo) => {
  if (data.diabetesType === "gestational") {
    return !!data.termDate;
  }
  return true;
};

const cannotBeType12AndNoInsulin = (data: SimpleCarePlanCreationInfo) => {
  if (data.diabetesType === "type1" || data.diabetesType === "type2") {
    return data.insulinScheme !== "noInsulin";
  }
  return true;
};

const cannotBeGestationalAndUsePump = (data: SimpleCarePlanCreationInfo) => {
  if (data.diabetesType === "gestational") {
    return data.insulinScheme !== "insulinPump";
  }
  return true;
};

const requiredInclusionCriteriaIfInsulinated = (data: SimpleCarePlanCreationInfo) => {
  if (data.insulinScheme !== "noInsulin") {
    return !!data.inclusionCriteria;
  }
  return true;
};

const requiredDurationIfInsulinated = (data: SimpleCarePlanCreationInfo) => {
  if (data.insulinScheme !== "noInsulin") {
    return !!data.duration;
  }
  return true;
};

const requiredDescriptionIfInsulinated = (data: SimpleCarePlanCreationInfo) => {
  if (data.insulinScheme !== "noInsulin") {
    return !!data.description || !!data.descriptionAttachment;
  }
  return true;
};

export const nurseInclusionPatientCreationInfoSchema = z
  .object({
    givenName: z.string("form.required").check(z.minLength(1)),
    familyName: z.string("form.required").check(z.minLength(1)),
    email: z.email("form.email.invalid"),
    phone: cz.phone({
      string: "form.required",
      refine: "form.phone.invalid",
    }),
    helperPhone: z.optional(
      cz.phone({
        refine: "form.phone.invalid",
      }),
    ),
    birthDate: z.iso.date("form.date.invalid").check(cz.validation.dateBefore(DateTime.now(), "form.date.future")),

    diabetesType: conditionTypeSchema,
    insulinScheme: medicationSchemeSchema,
    homeServiceProviderId: z.optional(z.string()),
    inclusionCriteria: z.optional(telemonitoringCriteriaSchema),
    duration: z.optional(z.literal(["1", "2", "3"])),
    description: z.optional(z.string()),
    descriptionAttachment: z.optional(fileSchema),
    treatmentPrescriptionAttachment: z.optional(fileSchema),
    referralLetterAttachment: z.optional(fileSchema),
    // Validated by the gestational-only refinements below: a hidden leftover must not block.
    termDate: z.optional(z.string()),
    periodStart: z.optional(z.string()),
    inclusionTier: z.optional(telemonitoringTierSchema),
    pump: z.optional(pumpDeviceBrandSchema),
    telemonitoringNurseId: z.optional(z.string()),
  })
  .check(
    z.refine(cannotBeType12AndNoInsulin, {
      path: ["insulinScheme"],
      error: "form.required",
    }),
    z.refine(cannotBeGestationalAndUsePump, {
      path: ["insulinScheme"],
      error: "form.required",
    }),
    z.refine(requiredHSPIfPump, {
      path: ["homeServiceProviderId"],
      error: "form.required",
    }),
    z.refine(requiredTermDateIfGestational, {
      path: ["termDate"],
      error: "form.required",
    }),
    z.refine(termDateIsValidIfGestational, {
      path: ["termDate"],
      error: "form.date.invalid",
    }),
    z.refine(termDateInFutureIfGestational, {
      path: ["termDate"],
      error: "form.date.past",
    }),
    z.refine(requiredInclusionCriteriaIfInsulinated, {
      path: ["inclusionCriteria"],
      error: "form.required",
    }),
    z.refine(requiredDurationIfInsulinated, {
      path: ["duration"],
      error: "form.required",
    }),
    z.refine(requiredDescriptionIfInsulinated, {
      path: ["description"],
      error: "diabetesForms.eitherObjectiveOrFile",
    }),
  );
